import React, {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle
} from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Card } from "../ui/card";
import { DecisionMatrixCardV2 } from "./decision-grid-v2";
import { API_BASE_URL } from "../../config/config";

// Replace this with your actual API endpoint
const STATUS_API_URL = `${API_BASE_URL}/insureai/ml/intake/applications/v2/check_decision_progress`;

const DecisionMatrixAIGuard = forwardRef(
  ({ triggerPollRef, onCommentsGuidanceChange }, ref) => {
    // Add display name for dev tools
    DecisionMatrixAIGuard.displayName = "DecisionMatrixAIGuard";
    const [status, setStatus] = useState(false);
    const [isPolling, setIsPolling] = useState(false);
    const navigate = useNavigate();

    const selectedApplicationId = useSelector(
      state => state.intakeData.selectedAppId
    );
    const accessToken = localStorage.getItem("access_token");
    const refreshToken = localStorage.getItem("refresh_token");

    // Expose triggerAnalysis method through ref
    useImperativeHandle(ref, () => ({
      triggerAnalysis: async () => {
        try {
          console.log("triggerAnalysis called in DecisionMatrixAIGuard");
          setIsPolling(true);
          setStatus(true);
          await pollApi();
        } catch (error) {
          console.error("Error triggering analysis:", error);
          setStatus(false);
          setIsPolling(false);
        }
      }
    }));

    // Polling logic
    const pollApi = React.useCallback(async () => {
      console.log("pollApi called, starting polling");
      if (!selectedApplicationId || !accessToken || !refreshToken) {
        console.log("Missing required data for polling");
        setStatus(false);
        setIsPolling(false);
        return;
      }

      const headers = {
        Accept: "application/json",
        "X-Access-Token": accessToken,
        "X-Refresh-Token": refreshToken
      };

      try {
        console.log("Making API call to:", STATUS_API_URL);
        const res = await fetch(
          `${STATUS_API_URL}?application_id=${selectedApplicationId}`,
          {
            method: "GET",
            headers: headers
          }
        );
        const data = await res.json();
        console.log("Polling response:", data);
        setStatus(data.inprogress);

        if (!data.inprogress) {
          console.log("Polling complete, inprogress is false");
          setIsPolling(false);
        }
      } catch (err) {
        console.error("Polling error:", err);
        setStatus(false);
        setIsPolling(false);
      }
    }, [accessToken, refreshToken, selectedApplicationId]);

    useEffect(() => {
      if (triggerPollRef) {
        triggerPollRef.current = () => pollApi();
      }
    }, [triggerPollRef, pollApi]);

    useEffect(() => {
      if (!selectedApplicationId || !accessToken || !refreshToken) {
        navigate("/");
        return;
      }

      let intervalId;

      if (isPolling) {
        console.log("Setting up polling interval");
        intervalId = setInterval(pollApi, 5000);
      }

      return () => {
        if (intervalId) {
          console.log("Cleaning up polling interval");
          clearInterval(intervalId);
        }
      };
    }, [
      selectedApplicationId,
      accessToken,
      refreshToken,
      navigate,
      isPolling,
      pollApi
    ]);

    if (status) {
      // Animated indeterminate progress bar
      const statusMsg = "Updating CMI mappings...";
      return (
        <Card className="flex flex-col items-center justify-center py-10 px-8 bg-white border-2 border-gray-200 w-full max-w-none shadow-lg">
          <div className="flex flex-col items-center w-full max-w-lg mx-auto">
            <div className="bg-orange-100 rounded-full p-4 mb-4">
              <svg
                className="h-10 w-10 text-orange-500"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <circle cx="12" cy="12" r="10" />
                <rect x="8" y="11" width="8" height="5" rx="1" />
                <rect x="10" y="7" width="4" height="4" rx="1" />
              </svg>
            </div>
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">
              Processing Data...
            </h2>
            <p className="text-gray-600 text-center mb-4 text-base">
              Please wait while we analyze and update Decision matrix data.
            </p>
            <div className="w-full mb-2">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-500">Processing...</span>
              </div>
              {/* Animated indeterminate progress bar */}
              <div className="w-full bg-gray-200 rounded-full h-3 mb-2 overflow-hidden relative">
                <div
                  className="absolute inset-0 animate-gradient-x bg-gradient-to-r from-orange-300 via-orange-500 to-orange-300 opacity-80 h-full w-1/2 rounded-full"
                  style={{ animation: "progress-wave 1.5s linear infinite" }}
                ></div>
              </div>
            </div>
            <div className="flex items-center justify-center w-full bg-white border border-gray-200 rounded-md py-3 px-4 mb-4">
              <svg
                className="h-5 w-5 text-gray-500 mr-2"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <circle cx="12" cy="12" r="10" />
                <rect x="8" y="11" width="8" height="5" rx="1" />
                <rect x="10" y="7" width="4" height="4" rx="1" />
              </svg>
              <span className="text-gray-700 text-sm font-medium">
                {statusMsg}
              </span>
            </div>
            <div className="flex items-center justify-center gap-2 mb-4">
              <span className="h-2 w-2 rounded-full bg-orange-500 inline-block"></span>
              <span className="h-2 w-2 rounded-full bg-orange-300 inline-block"></span>
              <span className="h-2 w-2 rounded-full bg-orange-200 inline-block"></span>
              <span className="h-2 w-2 rounded-full bg-gray-200 inline-block"></span>
              <span className="h-2 w-2 rounded-full bg-gray-200 inline-block"></span>
            </div>
            <div className="w-full">
              <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 text-xs rounded-md px-3 py-2 text-center">
                Approximate waiting time{" "}
                <span className="font-semibold">2 minutes</span> –
                Classifications and Decision Matrix are temporarily locked
                during processing.
              </div>
            </div>
          </div>
          <style>{`
          @keyframes progress-wave {
            0% { left: -50%; }
            100% { left: 100%; }
          }
          .animate-gradient-x {
            position: absolute;
            left: 0;
            top: 0;
            height: 100%;
            width: 50%;
            background: linear-gradient(90deg, #fdba74 0%, #ff5533 50%, #fdba74 100%);
            opacity: 0.8;
            border-radius: 9999px;
          }
        `}</style>
        </Card>
      );
    }
    return (
      <DecisionMatrixCardV2
        onCommentsGuidanceChange={onCommentsGuidanceChange}
      />
    );
  }
);

export default DecisionMatrixAIGuard;
